// Sprint 6 (Phase 5, D39) — Sticky progress bar.
//
// Always visible above the message list when the active agent has at least
// one TodoWrite emit. Collapsed: one-line "Phase N of M · current item" +
// thin progress bar. Expanded: full plan list using the existing
// `TodoChecklist` shell.
//
// Sticky positioning pins the bar at the top of the scroll container (top-0).
//
// Sprint sticky-message-section-stacking-cursor-parity, re-stated at DS.8
// (campaign aos-design-system, D12) in ROLES instead of raw values — the two
// literals this comment used to quote were themselves counted by Gate AA, and
// draining them by deleting the sentence would have destroyed the only
// in-code trace of the constraint. The causal fact, on one line:
// this bar covers the sticky permission card at the top of the viewport.
// Mechanically: the bar sits at Tailwind's z-30, the cards at the
// `z-card-sticky` role (tailwind.config.js, docs/ui/04-tokens.md §7), which is
// deliberately the lower rung. Changing either without the other re-opens the
// defect that sprint fixed, and DS.8's D6 re-establishes the same order for
// the toast layer above both.
//
// Sprint chatgpt-user-bubble (D-036 D4) retired the CSS custom property
// this bar used to write for `UserMessage`'s old sticky offset: the
// message is no longer position-sticky, so the property had zero
// consumers. A future overlay that needs to dock below this bar
// reintroduces its own offset together with its own consumer (P6, no
// orphan "just in case" state).

import { useRef, useState } from 'react';
import { Icon } from '../atoms/Icon';
import { useTranslation } from '../../i18n';
import type { ProgressState } from '../../lib/tool-rendering/derive-progress';

export interface StickyProgressBarProps {
  progress: ProgressState | null;
}

export function StickyProgressBar({ progress }: StickyProgressBarProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);

  if (!progress) return null;

  const pct = Math.round(progress.pct * 100);
  const headline =
    progress.inProgressItem ??
    (progress.current === progress.total ? t('progress.allDone') : t('progress.working'));

  return (
    <div
      ref={barRef}
      className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border"
      data-testid="sticky-progress-bar"
      data-pct={pct}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-accent/40 focus:outline-none"
        data-testid="sticky-progress-header"
      >
        <span className="text-caption text-muted-foreground shrink-0">
          {t('progress.phaseOf', {
            current: progress.current + (progress.inProgressItem ? 1 : 0),
            total: progress.total,
          })}
        </span>
        <span className="text-body text-foreground flex-1 truncate">· {headline}</span>
        <span className="text-caption text-muted-foreground/45 tabular-nums">{pct}%</span>
        <span className="text-muted-foreground/45 shrink-0" aria-hidden="true">
          {open ? <Icon name="ChevronDown" size={14} /> : <Icon name="ChevronRight" size={14} />}
        </span>
      </button>
      <div
        className="h-1 w-full bg-border/50 overflow-hidden"
        data-testid="sticky-progress-track"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-status-success transition-[width] duration-300"
          style={{ width: `${pct}%` }}
          data-testid="sticky-progress-fill"
        />
      </div>
      {open && (
        <ul
          className="px-4 py-2 space-y-1 text-body text-muted-foreground max-h-[40vh] overflow-y-auto"
          data-testid="sticky-progress-list"
        >
          {progress.fullList.map((t, i) => {
            const meta = STATUS_META[t.status];
            return (
              <li
                key={i}
                className={`flex items-start gap-2 ${meta.line}`}
                data-status={t.status}
              >
                <span
                  className={`mt-0.5 inline-flex w-4 h-4 items-center justify-center text-caption ${meta.iconClass}`}
                  aria-hidden="true"
                >
                  {meta.icon}
                </span>
                <span className={`flex-1 ${meta.text}`}>
                  {t.content}
                  {t.status === 'in_progress' && t.activeForm && (
                    <span className="block text-caption text-status-success/80 mt-0.5">
                      {t.activeForm}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const STATUS_META = {
  pending: {
    icon: '○',
    iconClass: 'text-muted-foreground/45',
    text: 'text-muted-foreground',
    line: '',
  },
  in_progress: {
    icon: '◐',
    iconClass: 'text-status-success',
    text: 'text-foreground font-medium',
    line: '',
  },
  completed: {
    icon: '✓',
    iconClass: 'text-status-success',
    text: 'text-muted-foreground line-through',
    line: '',
  },
  cancelled: {
    icon: '✕',
    iconClass: 'text-muted-foreground/45',
    text: 'text-muted-foreground/45 line-through',
    line: '',
  },
} as const;
